import styled from 'styled-components';
import MediaQuery from 'react-responsive';

import { useCallback } from 'react';
import { pick } from 'lodash';
import { darkerBlackPearl, firefly } from '@paradigm/design-system/src/colors';
import {
  useQuoteBuilderModalMethods,
  useQuoteBuilderModalState,
} from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/modal-context';
import {
  RfqProvider,
  useDrfqContext,
  useDrfqRfq,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import SubmittedProvider from '@paradigm/features/src/forms/submitted-context';
import { QuoteBuilderProvider } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/form-context';
import { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';

import HeadedModal from '@paradigm/design-system/src/components/HeadedModal';
import RfqStrategySummary, {
  ProductInfo,
} from '#/components/UnifiedRfqs/components/RfqStrategySummary';
import Grid from '#/components/UnifiedRfqs/components/Grid';

import useDrfqBbo from '@paradigm/features/src/unified-rfqs/contexts/use-drfq-bbo';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/drfq/quote-builder/view';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import MarkPrice from '#/components/UnifiedRfqs/components/MarkPrice';
import DesktopLegsTable from '#/components/UnifiedRfqs/components/LegsTable';
import MobileLegsTable, {
  ChangeLegPriceFn,
} from '#/components/UnifiedRfqs/components/MobileLegsTable';
import InputLegsPricesTable, {
  useChangeLegPriceAction,
} from '#/components/UnifiedRfqs/drfq/quote-builder/InputLegsPricesTable';
import { quoteModalSize } from '#/components/UnifiedRfqs/shared';
import QuoteBuilderForm from '#/components/UnifiedRfqs/drfq/quote-builder/QuoteBuilderForm';
import ScreenPrice from '#/components/UnifiedRfqs/drfq/quote-builder/ScreenPrice';
import StrategyGreeks from '#/components/UnifiedRfqs/components/StrategyGreeks';
import {
  AnonMessage,
  WarningMessage,
} from '#/components/UnifiedRfqs/components/WarningMessage';
import { RfqDateInfo } from '#/components/UnifiedRfqs/components/DetailsModal';
import NoRfqFound from '#/components/UnifiedRfqs/quote-builder/NoRfqFound';
import BboViewProvider from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';
import {
  TrackingCtxProvider,
  useTrackAction,
} from '@paradigm/features/src/analytics';
import DrfqMakersProvider from '@paradigm/features/src/unified-rfqs/ui/drfq/drfq-maker-orderbook-context';
import MakerBadge from './MakerBadge';
import { MakerBadgeContainer } from './atoms';

export default function QuoteBuilder() {
  const { rfqId, isOpen } = useQuoteBuilderModalState();

  if (!isOpen || rfqId == null) return null;

  return (
    <RfqProvider rfqId={rfqId}>
      <QuoteBuilderModal />
    </RfqProvider>
  );
}

export function QuoteBuilderModal() {
  const { rfq } = useDrfqContext();
  const { isOpen } = useQuoteBuilderModalState();
  const { close } = useQuoteBuilderModalMethods();

  if (rfq == null || rfq.state !== 'OPEN')
    return <NoRfqFound isOpen={isOpen} close={close} />;

  return (
    <SubmittedProvider>
      <DrfqMakersProvider drfq={rfq}>
        <QuoteBuilderProvider drfq={rfq}>
          <BboViewProvider>
            <TrackingCtxProvider value="rfq quote builder">
              <QuoteBuilderModalContent drfq={rfq} />
            </TrackingCtxProvider>
          </BboViewProvider>
        </QuoteBuilderProvider>
      </DrfqMakersProvider>
    </SubmittedProvider>
  );
}

function QuoteBuilderModalContent({ drfq }: { drfq: Drfq }) {
  const { close } = useQuoteBuilderModalMethods();
  const { isOpen, legPricingEnabled, isMarkPricePrefill } =
    useQuoteBuilderModalState();
  const { isAnonymousRfq } = useQuoteBuilderView();
  const trackAction = useTrackAction();
  const handleClose = useCallback(() => {
    trackAction('close modal');
    close();
  }, [close, trackAction]);

  const title =
    isMarkPricePrefill && legPricingEnabled
      ? 'Enter Leg Prices Manually'
      : 'Quote';

  return (
    <HeadedModal
      title={title}
      isOpen={isOpen}
      close={handleClose}
      leftInfo={<LeftInfo drfq={drfq} />}
      rightInfo={
        <MakerBadgeContainer>
          <MakerBadge />
        </MakerBadgeContainer>
      }
      width={quoteModalSize(legPricingEnabled, true)}
      fade={false}
      aria-label="drfq quote builder modal"
    >
      <Container isAnon={isAnonymousRfq}>
        <Grid cols={1} css="padding: 1rem; flex: 1;">
          <RfqStrategySummary rfq={drfq} />
          <MediaQuery minWidth={MOBILE_BREAKPOINT}>
            <RfqDateInfo date={drfq.last_updated_at} />
          </MediaQuery>
          <MediaQuery maxWidth={MOBILE_BREAKPOINT}>
            <MarkPrice />
          </MediaQuery>
          <LegsTable />
          <ScreenPrice data-testid="drfq-maker-screen-prices" />
          <MediaQuery maxWidth={MOBILE_BREAKPOINT}>
            <GreeksContainer>
              <Greeks drfq={drfq} />
            </GreeksContainer>
          </MediaQuery>
          {isAnonymousRfq && (
            <WarningContainer>
              <WarningMessage>
                <AnonMessage />
              </WarningMessage>
            </WarningContainer>
          )}
        </Grid>
        <RightContainer>
          <QuoteBuilderForm greeks={<Greeks drfq={drfq} />} />
        </RightContainer>
      </Container>
    </HeadedModal>
  );
}

function LegsTable() {
  const isMobile = useIsMobile();
  const { legs, legsPrices } = useQuoteBuilderView();
  const { legPricingEnabled } = useQuoteBuilderModalState();
  const changeLegPrice = useChangeLegPriceAction();
  const drfq = useDrfqRfq();
  const bbo = useDrfqBbo(drfq.id);
  const markPrices = bbo?.legs.map((leg) => leg.mark_price) ?? [];
  const side = 'BUY';
  const trackAction = useTrackAction();
  const handleChangeLegPrice = useCallback<ChangeLegPriceFn>(
    (args) => {
      trackAction('change leg price', pick(args, ['index', 'side']));
      changeLegPrice(args);
    },
    [changeLegPrice, trackAction],
  );
  return isMobile ? (
    <MobileLegsTable
      legs={legs}
      legPrices={legsPrices.legs}
      changeLegPrice={handleChangeLegPrice}
      markPrices={markPrices}
      legPricingEnabled={legPricingEnabled}
      title="Strategy Details (Buyer)"
      isTwoWay
    />
  ) : (
    <div css="box-sizing:border-box; display: grid; grid-template-columns: 1fr min-content min-content">
      <DesktopLegsTable side={side} legs={legs} />
      <InputLegsPricesTable />
    </div>
  );
}

function Greeks({ drfq }: { drfq: Drfq }) {
  const bbo = useDrfqBbo(drfq.id);
  return <StrategyGreeks bbo={bbo} venue={drfq.venue} />;
}

function LeftInfo({ drfq }: { drfq: Drfq }) {
  const isMobile = useIsMobile();
  return isMobile ? (
    <RfqDateInfo date={drfq.last_updated_at} />
  ) : (
    <ProductInfo venue={drfq.venue} productCode={drfq.product_codes[0]} />
  );
}

const WarningContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
`;

const Container = styled.div<{ isAnon: boolean }>`
  display: flex;
  ${(props) => props.isAnon && `background-color: black;`}

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: grid;

    .mark-container {
      margin: 0 auto;
      padding-top: 0.375rem;

      span {
        font-size: 0.875rem;
        line-height: 1rem;
      }
    }

    .mark-row-container {
      grid-template-columns: 2rem auto;
    }

    &&& .strategy-greeks {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      > * {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }

    &&& .strategy-greeks-cell {
      font-weight: normal;
      font-size: 0.875rem;
      line-height: 1rem;
    }

    .strategy-greeks-title {
      font-size: 1rem;
      line-height: 1.25rem;
      font-weight: normal;
      padding: 0;
    }
  }
`;

const RightContainer = styled.div`
  width: 222px;
  background-color: ${firefly};
  border-radius: 0 0 10px 0;
  padding: 1rem;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    border-radius: 0;
    background-color: transparent;
    padding-top: 0;
  }
`;

const GreeksContainer = styled.div`
  background-color: ${darkerBlackPearl};
  border-radius: 0.25rem;
  padding: 1rem;
`;
